import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import type { ShopListItem, Thumbnail } from './api';

/**
 * Oblíbené žijí v prohlížeči, stejně jako košík.
 *
 * Uloha je „schovej si to na potom", ne „drž mi to za aktuální cenu" –
 * proto se ukládá jen to, co je potřeba k zobrazení dlaždice, a živá data
 * (sklad, cena) si zákazník vyzvedne až na detailu. Žádné přihlášení:
 * srovnávat oblíbené se serverem by znamenalo účty, které obchod nemá.
 */

export interface FavoriteItem {
  readonly id: string;
  readonly slug: string;
  readonly name: string;
  readonly brand: string | null;
  readonly imageUrl: string | null;
  readonly imageThumbnails: readonly Thumbnail[];
  readonly priceFromMinor: number;
  readonly priceToMinor: number;
  readonly compareAtPriceFromMinor: number | null;
}

interface FavoritesState {
  readonly items: readonly FavoriteItem[];
  readonly count: number;
  has(id: string): boolean;
  toggle(product: ShopListItem): void;
  remove(id: string): void;
}

const STORAGE_KEY = 'zooshopik.favorites.v1';

const FavoritesContext = createContext<FavoritesState | null>(null);

function readStorage(): FavoriteItem[] {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === null) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Data z localStorage muze upravit kdokoli. Kazdou polozku proto
    // NORMALIZUJEME na kompletni tvar – stranka oblibenych cte i obrazky
    // a ceny, takze chybejici pole (stara nebo rucne rozbita data) by ji
    // shodilo. Co nedava smysl, se zahodi; zbytek dostane bezpecnou vychozi.
    const out: FavoriteItem[] = [];
    for (const entry of parsed) {
      if (typeof entry !== 'object' || entry === null) continue;
      const item = entry as Record<string, unknown>;
      if (typeof item['id'] !== 'string' || typeof item['slug'] !== 'string') continue;
      out.push({
        id: item['id'],
        slug: item['slug'],
        name: typeof item['name'] === 'string' ? item['name'] : '',
        brand: typeof item['brand'] === 'string' ? item['brand'] : null,
        imageUrl: typeof item['imageUrl'] === 'string' ? item['imageUrl'] : null,
        imageThumbnails: Array.isArray(item['imageThumbnails'])
          ? (item['imageThumbnails'] as FavoriteItem['imageThumbnails'])
          : [],
        priceFromMinor: typeof item['priceFromMinor'] === 'number' ? item['priceFromMinor'] : 0,
        priceToMinor: typeof item['priceToMinor'] === 'number' ? item['priceToMinor'] : 0,
        compareAtPriceFromMinor:
          typeof item['compareAtPriceFromMinor'] === 'number' ? item['compareAtPriceFromMinor'] : null,
      });
    }
    return out;
  } catch {
    // Privatni rezim nebo zakazane uloziste – oblibene proste zacnou prazdne.
    return [];
  }
}

/** Z produktu ve výpisu uloží jen to, co dlaždice v oblíbených potřebuje. */
function toFavorite(product: ShopListItem): FavoriteItem {
  return {
    id: product.id,
    slug: product.slug,
    name: product.name,
    brand: product.brand,
    imageUrl: product.imageUrl,
    imageThumbnails: product.imageThumbnails,
    priceFromMinor: product.priceFromMinor,
    priceToMinor: product.priceToMinor,
    compareAtPriceFromMinor: product.compareAtPriceFromMinor,
  };
}

export function FavoritesProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<FavoriteItem[]>(readStorage);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      // Uloziste plne nebo zakazane – oblibene zustanou aspon v pameti.
    }
  }, [items]);

  // Změna v jiné záložce (přidání oblíbeného) se promítne i sem.
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (event.key === STORAGE_KEY) setItems(readStorage());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const has = useCallback((id: string) => items.some((item) => item.id === id), [items]);

  const toggle = useCallback((product: ShopListItem) => {
    setItems((current) =>
      current.some((item) => item.id === product.id)
        ? current.filter((item) => item.id !== product.id)
        : [toFavorite(product), ...current],
    );
  }, []);

  const remove = useCallback((id: string) => {
    setItems((current) => current.filter((item) => item.id !== id));
  }, []);

  const value = useMemo<FavoritesState>(
    () => ({ items, count: items.length, has, toggle, remove }),
    [items, has, toggle, remove],
  );

  return <FavoritesContext.Provider value={value}>{children}</FavoritesContext.Provider>;
}

export function useFavorites(): FavoritesState {
  const context = useContext(FavoritesContext);
  if (context === null) throw new Error('useFavorites musí být uvnitř FavoritesProvider.');
  return context;
}
